import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Rocket, Network, Vault, ShieldCheck, ArrowRight } from "lucide-react";

const iconMap = {
  rocket: Rocket,
  network: Network,
  vault: Vault,
  shield: ShieldCheck,
};

const serviceItems = [
  {
    id: "startup",
    title: "Startup Advisory",
    description: "Venture-readiness assessment, pitch deck optimization, and strategic positioning for Series A and beyond.",
    iconName: "rocket",
    feature1: "Fundraising Strategy",
    feature2: "Investor Relations",
    feature3: "Growth Planning",
  },
  {
    id: "consulting",
    title: "Business Consultant",
    description: "Operational efficiency frameworks and scalable business model design for high-growth companies.",
    iconName: "network",
    feature1: "Process Optimization",
    feature2: "Market Expansion",
    feature3: "Performance Metrics",
  },
  {
    id: "cfo",
    title: "CFO Services",
    description: "Fractional CFO expertise for liquidity management, financial governance, and board-level reporting.",
    iconName: "vault",
    feature1: "Cash Flow Management",
    feature2: "Financial Modeling",
    feature3: "Board Reporting",
  },
  {
    id: "tax",
    title: "Taxation & Investments",
    description: "Strategic tax planning and wealth preservation strategies for founders and high-net-worth individuals.",
    iconName: "shield",
    feature1: "Tax Optimization",
    feature2: "Asset Protection",
    feature3: "Estate Planning",
  },
];

export const ServicesGrid = () => {
  return (
    <section data-testid="services-grid" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-16"
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Our Services
          </span>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            The Core Four
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            Comprehensive financial solutions tailored for ambitious founders and 
            growth-stage companies.
          </p>
        </motion.div>

        {/* Services Grid - 2x2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {serviceItems.map((service, index) => {
            const IconComponent = iconMap[service.iconName];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/services#${service.id}`}
                  data-testid={`service-card-${service.id}`}
                  className="service-card group relative overflow-hidden rounded-2xl p-8 md:p-10 bg-[#0A0A0A]/80 border border-white/5 hover:border-[#00C896]/30 transition-all duration-500 block h-full"
                >
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-xl bg-[#00C896]/10 border border-[#00C896]/20 flex items-center justify-center mb-6 group-hover:bg-[#00C896]/20 transition-colors">
                    <IconComponent className="w-6 h-6 text-[#00C896]" />
                  </div>

                  {/* Content */}
                  <h3
                    className="text-2xl font-semibold text-white mb-4"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="text-xs font-mono text-slate-500 bg-white/5 px-3 py-1 rounded-full">
                      {service.feature1}
                    </span>
                    <span className="text-xs font-mono text-slate-500 bg-white/5 px-3 py-1 rounded-full">
                      {service.feature2}
                    </span>
                    <span className="text-xs font-mono text-slate-500 bg-white/5 px-3 py-1 rounded-full">
                      {service.feature3}
                    </span>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center gap-2 text-[#00C896] text-sm font-medium">
                    <span>Learn More</span>
                    <ArrowRight
                      size={16}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </div>

                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#00C896]/5 to-transparent" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
